
import { defineComponent, onMounted, ref, computed } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import { Actions } from "@/store/enums/StoreEnums";
import { useToast } from "vue-toastification";
import Swal from "sweetalert2/dist/sweetalert2.min.js";

interface Plan {
  key: string;
  plan: string;
  interval: string;
  price: number;
  label: string;
  integrationLimit: number | null;
}

export default defineComponent({
  name: "billing",
  setup() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const toast = useToast();

    const plans = ref<Plan[]>([]);
    const interval = ref<"month" | "year">("month");
    const loadingKey = ref<string | null>(null);
    const stats = ref<any>(null);

    const subscription = computed(() => store.getters.getSubscription);

    const tiers = [
      {
        plan: "standard",
        limit: 3,
        title: "3 Entegrasyona Kadar",
        subtitle: "Küçük işletmeler için ideal başlangıç paketi.",
        features: [
          "En fazla 3 entegrasyon",
          "Ürün açma / kapama",
          "Fiyat güncelleme",
          "Yorum yönetimi",
          "Restoran açma / kapama",
        ],
      },
      {
        plan: "unlimited",
        limit: null as number | null,
        title: "Sınırsız Entegrasyon",
        subtitle: "Çok şubeli ve çok platformlu işletmeler için.",
        features: [
          "Sınırsız entegrasyon",
          "Ürün açma / kapama",
          "Fiyat güncelleme",
          "Yorum yönetimi",
          "Restoran açma / kapama",
          "Öncelikli destek",
        ],
      },
    ];

    const keyFor = (plan: string) => `${plan}_${interval.value}`;
    const planObjFor = (plan: string) =>
      plans.value.find((p) => p.plan === plan && p.interval === interval.value);
    const priceFor = (plan: string) => {
      const p = planObjFor(plan);
      return p ? p.price : "—";
    };
    const isCurrent = (plan: string) =>
      subscription.value &&
      subscription.value.active &&
      subscription.value.plan === plan &&
      subscription.value.interval === interval.value;

    // A capped tier (e.g. 3-integration plan) is not selectable when the user
    // already has more integrations than the cap.
    const notEligible = (tier: { limit: number | null }) =>
      tier.limit != null &&
      subscription.value &&
      typeof subscription.value.integrationCount === "number" &&
      subscription.value.integrationCount > tier.limit;

    const planLabel = (plan: string | null) =>
      plan === "unlimited"
        ? "Sınırsız Entegrasyon"
        : plan === "standard"
        ? "3 Entegrasyona Kadar"
        : "—";

    // Thousands separator (Turkish locale -> 181.403).
    const fmt = (n: number) =>
      typeof n === "number" ? n.toLocaleString("tr-TR") : n;

    // Calendar-accurate "X yıl Y ay Z gün" from the first-activity date.
    const activeDuration = (since: string) => {
      if (!since) return "";
      const start = new Date(since);
      const now = new Date();
      let years = now.getFullYear() - start.getFullYear();
      let months = now.getMonth() - start.getMonth();
      let days = now.getDate() - start.getDate();
      if (days < 0) {
        months -= 1;
        days += new Date(now.getFullYear(), now.getMonth(), 0).getDate();
      }
      if (months < 0) {
        years -= 1;
        months += 12;
      }
      const parts: string[] = [];
      if (years > 0) parts.push(`${years} yıl`);
      if (months > 0) parts.push(`${months} ay`);
      if (days > 0) parts.push(`${days} gün`);
      return parts.length ? parts.join(" ") : "bugün";
    };

    // Estimated time saved: every manual open/close/price action on a platform's
    // own panel takes ~1 min per 2 actions (i.e. 30 sn each); on TekPanel it's a
    // single click. Counts product/option toggles and price updates.
    const SECONDS_PER_ACTION = 30;
    const minutesSaved = computed(() => {
      if (!stats.value) return 0;
      const actions =
        (stats.value.productToggleCount || 0) +
        (stats.value.optionToggleCount || 0) +
        (stats.value.priceUpdateCount || 0);
      return Math.round((actions * SECONDS_PER_ACTION) / 60);
    });
    // A "gün" here is an 8-hour work day, so the saved time reads as work-days.
    const WORKDAY_MINUTES = 8 * 60;
    const formatDuration = (mins: number) => {
      if (!mins || mins <= 0) return "0 dakika";
      const days = Math.floor(mins / WORKDAY_MINUTES);
      const hours = Math.floor((mins % WORKDAY_MINUTES) / 60);
      const minutes = Math.round(mins % 60);
      const parts: string[] = [];
      if (days) parts.push(`${fmt(days)} iş günü`);
      if (hours) parts.push(`${hours} saat`);
      if (minutes && !days) parts.push(`${minutes} dakika`);
      return parts.join(" ") || "0 dakika";
    };

    const formatDate = (d: string) => {
      try {
        return new Date(d).toLocaleDateString("tr-TR");
      } catch {
        return d;
      }
    };

    const subscribe = async (plan: string) => {
      const key = keyFor(plan);
      loadingKey.value = key;
      try {
        const res: any = await store.dispatch(Actions.CREATE_CHECKOUT, key);
        if (res && res.url) {
          window.location.href = res.url;
        } else {
          toast.error("Ödeme başlatılamadı, lütfen tekrar deneyin.");
          loadingKey.value = null;
        }
      } catch (e: any) {
        loadingKey.value = null;
        const msg =
          e?.response?.data?.message ||
          "Ödeme başlatılamadı. Sunucuda Stripe ayarları eksik olabilir.";
        toast.error(msg);
      }
    };

    const cancelSubscription = async () => {
      const res = await Swal.fire({
        title: "Aboneliği iptal et?",
        text: "Mevcut dönem sonuna kadar erişiminiz devam eder.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Evet, iptal et",
        cancelButtonText: "Vazgeç",
      });
      if (!res.value) return;
      try {
        await store.dispatch(Actions.CANCEL_SUBSCRIPTION);
        toast.success("Aboneliğiniz dönem sonunda sonlandırılacak.");
      } catch {
        toast.error("İptal işlemi başarısız oldu.");
      }
    };

    onMounted(async () => {
      plans.value = (await store.dispatch(Actions.GET_PLANS)) as Plan[];
      await store.dispatch(Actions.GET_SUBSCRIPTION);
      store.dispatch(Actions.GET_USAGE_STATS).then((s) => (stats.value = s));

      // Returning from Stripe Checkout?
      const status = route.query.status as string;
      const sessionId = route.query.session_id as string;
      if (status === "success" && sessionId) {
        try {
          await store.dispatch(Actions.CONFIRM_CHECKOUT, sessionId);
          toast.success("Aboneliğiniz başarıyla etkinleştirildi!");
        } catch {
          // Webhook will reconcile shortly; just refresh.
          await store.dispatch(Actions.GET_SUBSCRIPTION);
        }
        router.replace({ path: "/billing" });
      } else if (status === "cancel") {
        toast.info("Ödeme iptal edildi.");
        router.replace({ path: "/billing" });
      }
    });

    return {
      plans,
      interval,
      tiers,
      subscription,
      stats,
      loadingKey,
      keyFor,
      priceFor,
      isCurrent,
      notEligible,
      planLabel,
      formatDate,
      fmt,
      activeDuration,
      minutesSaved,
      formatDuration,
      subscribe,
      cancelSubscription,
    };
  },
});
